import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import { getEmployeeDetails, getEmployeeOvertimeStats, deleteEmployeeCache, getHolidays, createOvertimePermission } from "../services/apiService";
import { Employee, OvertimeStats } from "../types";
import VacationHistory from "./VacationHistory";
import WorkScheduleHistory from "./WorkScheduleHistory";
import VacationRequestList from "./VacationRequestList";
import OvertimePermissionsManager from "./OvertimePermissionsManager";
import EmployeeHeader from "./EmployeeHeader";
import StatsCards from "./StatsCards";
import OvertimeStatisticsTable from "./OvertimeStatisticsTable";
import ActionButton from "./ActionButton";
import { useAuth } from "./AuthContext";
import "./EmployeeDetail.css";
import { downloadExcel, formatTimeForExcel, formatDateForExcel } from "../utils/excelExport";
import { downloadHtml } from "../utils/htmlExport";
import { downloadCSV, formatTimeForCSV, formatDateForCSV } from "../utils/csvExport";
import {
  getCurrentAverageWorkingMinutesPerDay,
  calculateFTEVacationDays,
} from "../utils/timeFormatting";
import { calculateDailyVacationUsage } from "../utils/summaryCalculations";

interface EmployeeDetailProps {
  employeeName?: string;
}

const EmployeeDetail: React.FC<EmployeeDetailProps> = ({ employeeName: propEmployeeName }) => {
  const { employeeName: urlEmployeeName } = useParams<{ employeeName: string }>();
  const employeeName = propEmployeeName || urlEmployeeName;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overtimeStats, setOvertimeStats] = useState<OvertimeStats[]>([]);
  const [employeeDetails, setEmployeeDetails] = useState<Employee | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [holidays, setHolidays] = useState<Record<string, string>>({});
  const [permissionRefreshTrigger, setPermissionRefreshTrigger] = useState<number>(0);
  const [showExportDropdown, setShowExportDropdown] = useState<boolean>(false);

  const isAdmin = user?.isAdmin || false;

  const fetchData = async (showLoading = true) => {
    if (!employeeName) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      const stats = await getEmployeeOvertimeStats(employeeName);
      setOvertimeStats(stats);

      try {
        const details = await getEmployeeDetails(employeeName);
        setEmployeeDetails(details);
      } catch (err) {
        console.warn("Could not fetch employee details:", err);
        // Still continue even if details can't be loaded
      }

      try {
        const holidaysData = await getHolidays();
        setHolidays(holidaysData);
      } catch (err) {
        console.warn("Could not fetch holidays:", err);
        // Still continue even if holidays can't be loaded
      }

      if (showLoading) {
        setLoading(false);
      }
    } catch (err: any) {
      if (err.response && err.response.status === 403) {
        setError("Du hast keine Berechtigung, die Statistiken dieses Mitarbeiters anzusehen.");
        setLoading(false);
        return;
      }
      if (err.response && err.response.status === 404) {
        setError("Mitarbeiter nicht gefunden.");
        setLoading(false);
        return;
      }
      console.error("Error fetching overtime stats:", err);
      setError("Fehler beim Laden der Mitarbeiterstatistiken. Bitte später erneut versuchen.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeName) {
      document.title = `${employeeName} - Kiwi Overtime Tracker`;
    }
  }, [employeeName]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-export-dropdown]')) {
          setShowExportDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  const handleDataChange = () => {
    // Refresh employee data when work schedules or permissions are modified
    // Use false to avoid showing loading state and scroll jump
    fetchData(false);
  };

  const handleRefresh = async () => {
    if (!employeeName) return;

    try {
      setLoading(true);
      // Delete the cache for this employee
      await deleteEmployeeCache(employeeName);
      // Then refresh the data
      await fetchData();
    } catch (error: any) {
      console.error("Error refreshing data:", error);
      // Still try to fetch data even if cache deletion fails
      await fetchData();
    }
  };

  const handleAddPermission = async (date: string) => {
    if (!employeeDetails) return;

    try {
      // Create a single-day permission (start and end date are the same)
      await createOvertimePermission(employeeDetails.userId, date, date);
      // Refresh the data without showing loading state to avoid scroll jump
      await fetchData(false);
      // Trigger permission manager refresh
      setPermissionRefreshTrigger(prev => prev + 1);
    } catch (error: any) {
      console.error("Error adding overtime permission:", error);
      alert("Fehler beim Hinzufügen der Überstundengenehmigung");
    }
  };


  const handleExportExcel = () => {
    if (!overtimeStats || overtimeStats.length === 0) {
      alert("Keine Daten zum Exportieren verfügbar");
      return;
    }
    setShowExportDropdown(false);

    const headers = [
      "Datum",
      "Erwartete Stunden",
      "Gearbeitete Stunden",
      "Krankheit",
      "Genutzter Urlaub (Zeit)",
      "Genutzter Urlaub (FTE)",
      "Verbleibender Urlaub (Zeit)",
      "Verbleibender Urlaub (FTE)",
      "Überstunden Tag",
      "Überstunden gesamt",
    ];

    const rows = [...overtimeStats].reverse().map((stat, index, array) => {
      const nextStat = index < array.length - 1 ? array[index + 1] : null;
      const dailyUsedVacationMinutes = calculateDailyVacationUsage(stat, nextStat);

      return [
        formatDateForExcel(new Date(stat.date)),
        formatTimeForExcel(stat.expectedMinutesWorked),
        formatTimeForExcel(stat.totalMinutesWorked),
        formatTimeForExcel(stat.sickMinutes),
        formatTimeForExcel(dailyUsedVacationMinutes),
        calculateFTEVacationDays(dailyUsedVacationMinutes),
        formatTimeForExcel(stat.remainingVacationMinutes),
        calculateFTEVacationDays(stat.remainingVacationMinutes),
        formatTimeForExcel(stat.overOrUnderTimeMinutes),
        formatTimeForExcel(stat.totalOverOrUndertime),
      ];
    });

    const filename = `${employeeName?.replace(/\s+/g, "_")}_overtime_statistics_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    // Columns with duration/time format: all except Datum (0) and FTE columns (5, 7)
    const durationColumns = [1, 2, 3, 4, 6, 8, 9];

    downloadExcel({ headers, rows, filename, durationColumns });
  };

  const handleExportHtml = () => {
    if (!overtimeStats || overtimeStats.length === 0) {
      alert("Keine Daten zum Exportieren verfügbar");
      return;
    }

    downloadHtml({
      overtimeStats,
      employeeName: employeeName || "Unbekannt",
      holidays,
    });
    setShowExportDropdown(false);
  };

  const handleExportCSV = () => {
    if (!overtimeStats || overtimeStats.length === 0) {
      alert("Keine Daten zum Exportieren verfügbar");
      return;
    }
    setShowExportDropdown(false);

    const headers = [
      "Datum",
      "Erwartete Stunden",
      "Gearbeitete Stunden",
      "Krankheit",
      "Genutzter Urlaub (Zeit)",
      "Genutzter Urlaub (FTE)",
      "Verbleibender Urlaub (Zeit)",
      "Verbleibender Urlaub (FTE)",
      "Überstunden Tag",
      "Überstunden gesamt",
    ];

    const rows = [...overtimeStats].reverse().map((stat, index, array) => {
      const nextStat = index < array.length - 1 ? array[index + 1] : null;
      const dailyUsedVacationMinutes = calculateDailyVacationUsage(stat, nextStat);

      return [
        formatDateForCSV(new Date(stat.date)),
        formatTimeForCSV(stat.expectedMinutesWorked),
        formatTimeForCSV(stat.totalMinutesWorked),
        formatTimeForCSV(stat.sickMinutes),
        formatTimeForCSV(dailyUsedVacationMinutes),
        calculateFTEVacationDays(dailyUsedVacationMinutes),
        formatTimeForCSV(stat.remainingVacationMinutes),
        calculateFTEVacationDays(stat.remainingVacationMinutes),
        formatTimeForCSV(stat.overOrUnderTimeMinutes),
        formatTimeForCSV(stat.totalOverOrUndertime),
      ];
    });

    const filename = `${employeeName?.replace(/\s+/g, "_")}_overtime_statistics_${
      new Date().toISOString().split("T")[0]
    }.csv`;

    downloadCSV({ headers, rows, filename });
  };

  if (!employeeName) {
    return <div className="error">Kein Mitarbeiter angegeben.</div>;
  }

  if (loading) {
    return <div className="loading">Mitarbeiterstatistiken werden geladen...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const latestStats = overtimeStats.length > 0 ? overtimeStats[overtimeStats.length - 1] : null;
  const averageMinutesPerDay = getCurrentAverageWorkingMinutesPerDay(employeeDetails);

  return (
    <div className="container">
      <div className="employee-detail-card">
        <EmployeeHeader
          employeeName={employeeName || ""}
          loading={loading}
          onBack={propEmployeeName ? undefined : () => navigate(-1)}
          onRefresh={handleRefresh}
        />

        {latestStats && (
          <>
            <div className="section-divider"></div>
            <StatsCards
              totalOvertime={latestStats.totalOverOrUndertime}
              remainingVacation={latestStats.remainingVacationMinutes}
              plannedVacation={latestStats.plannedVacationMinutes}
            />
          </>
        )}

        {/* Work Schedule History */}
        {employeeDetails && employeeDetails.plannedMinutesPerDayList && (
          <div className="section-content">
            <WorkScheduleHistory
              workSchedules={employeeDetails.plannedMinutesPerDayList}
              employeeUserId={employeeDetails.userId}
              employeeName={employeeDetails.name}
              isAdmin={isAdmin}
              onDataChange={handleDataChange}
            />
          </div>
        )}

        {/* Overtime Permissions (Admin Only) */}
        {isAdmin && employeeDetails && (
          <div className="section-content">
            <OvertimePermissionsManager
              employeeUserId={employeeDetails.userId}
              employeeName={employeeDetails.name}
              onDataChange={handleDataChange}
              refreshTrigger={permissionRefreshTrigger}
            />
          </div>
        )}

        {/* Vacation History */}
        {employeeDetails && employeeDetails.agreedVacationDays && (
          <div className="section-content">
            <VacationHistory vacationEntries={employeeDetails.agreedVacationDays} />
          </div>
        )}

        {/* Vacation Requests */}
        {employeeDetails && (
          <div className="section-content">
            <VacationRequestList
              employeeUserId={employeeDetails.userId}
              showAddButton={!isAdmin}
              isAdmin={isAdmin}
              onDataChange={handleDataChange}
            />
          </div>
        )}

        {/* Detailed Statistics */}
        <div className="section-content">
          <div className="section-header">
            <h2>Detaillierte Statistiken</h2>
            {overtimeStats.length > 0 && (
              <div style={{ position: 'relative' }} data-export-dropdown>
                <ActionButton
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  icon={FaDownload({})}
                >
                  Exportieren
                </ActionButton>
                {showExportDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      zIndex: 1000,
                      minWidth: '200px'
                    }}
                  >
                    <button
                      onClick={handleExportExcel}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      📊 Excel exportieren
                    </button>
                    <button
                      onClick={handleExportHtml}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      🌐 HTML exportieren
                    </button>
                    <button
                      onClick={handleExportCSV}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      📄 CSV exportieren
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="stats-table">
            <OvertimeStatisticsTable
              overtimeStats={overtimeStats}
              averageMinutesPerDay={averageMinutesPerDay}
              holidays={holidays}
              isAdmin={isAdmin}
              employeeUserId={employeeDetails?.userId}
              onAddPermission={handleAddPermission}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetail;